import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SSE_URL } from '../api';
import { useUser } from './UserContext';

const SseContext = createContext(null);

// Single shared EventSource for the whole tab.
//
// Chrome caps an HTTP/1.1 host at 6 concurrent connections, shared across
// tabs. The SPA used to open one long-lived SSE stream per dashboard page
// PLUS one on the always-mounted NavHeader — those streams hit the same
// :3000 origin as file uploads, so they could saturate the pool and starve
// the upload POST of a socket (uploads appeared to hang). Routing every
// consumer through ONE EventSource via this provider keeps the SSE footprint
// at a single connection.
export function SseProvider({ children }) {
  const { currentUser } = useUser();
  const [sseConnected, setSseConnected] = useState(false);

  // Subscriber handlers. Held in a ref (not state) so adding/removing a
  // subscriber never re-renders the provider or reconnects the stream.
  const subscribersRef = useRef(new Set());

  useEffect(() => {
    // Connect ONLY when logged in. The public ApproveAll page and the
    // LoginPage have no currentUser, so they hold zero sockets. Logout
    // (currentUser -> null) tears the stream down via cleanup.
    if (!currentUser) {
      setSseConnected(false);
      return undefined;
    }

    let cancelled = false;
    let es = null;
    let retryDelay = 2000;
    const MAX_RETRY_DELAY = 30000;

    function connect() {
      if (cancelled) return;
      es = new EventSource(SSE_URL);
      es.onopen = () => {
        setSseConnected(true);
        retryDelay = 2000;
      };
      es.onmessage = (e) => {
        // Fan out to every subscriber. Wrap each call so one throwing
        // handler can't break delivery to the others.
        subscribersRef.current.forEach((handler) => {
          try {
            handler(e);
          } catch {
            // ignore a single bad subscriber
          }
        });
      };
      es.onerror = () => {
        setSseConnected(false);
        es.close();
        es = null;
        if (!cancelled) {
          setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
        }
      };
    }

    connect();
    return () => {
      // KEEP this guard: under React.StrictMode the effect double-invokes,
      // and cancelled + close-in-cleanup prevents an orphaned stream.
      cancelled = true;
      if (es) es.close();
      setSseConnected(false);
    };
    // Reconnect only when the logged-in identity changes — not on every
    // currentUser object identity change. Depending on the whole object
    // would needlessly tear down and rebuild the stream.
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((handler) => {
    subscribersRef.current.add(handler);
    return () => subscribersRef.current.delete(handler);
  }, []);

  const value = useMemo(
    () => ({ sseConnected, subscribe }),
    [sseConnected, subscribe]
  );

  return <SseContext.Provider value={value}>{children}</SseContext.Provider>;
}

export function useSse() {
  return useContext(SseContext);
}

// Subscribe a handler to the shared stream for the lifetime of the calling
// component. The handler identity may change every render (e.g. a `loadData`
// recreated each render) without resubscribing: we stash the latest handler
// in a ref and subscribe a STABLE wrapper exactly once on mount.
export function useSseSubscription(handler) {
  const { subscribe } = useSse();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    const unsubscribe = subscribe((e) => ref.current && ref.current(e));
    return unsubscribe;
  }, [subscribe]);
}

export default SseContext;
